export type RepairStatusColorMode = 'light' | 'dark';

export type RepairStatusColors = {
  bg: string;
  text: string;
  /** מסגרת עדינה — מדגיש את התג על רקע בהיר/כהה */
  border?: string;
  /**
   * מילוי מוצק לעיגולי סטפר / הדגשות — חייב להיות קריא עם טקסט לבן מעליו.
   * במצב בהיר בדרך־כלל זהה ל־text; במצב כהה זה צבע רווי נפרד מטקסט התג.
   */
  accent?: string;
};

const LIGHT: Record<string, RepairStatusColors> = {
  labPending: {
    bg: '#e3f2fd',
    text: '#1565c0',
    border: '1px solid rgba(21, 101, 192, 0.45)',
  },
  returned: {
    bg: '#eceff1',
    text: '#546e7a',
    border: '1px solid rgba(84, 110, 122, 0.4)',
  },
  'התקבל, ממתין לטיפול': {
    bg: '#e3f2fd',
    text: '#1565c0',
    border: '1px solid rgba(21, 101, 192, 0.45)',
  },
  'ממתין למשלוח חזרה': {
    bg: '#ede7f6',
    text: '#6a1b9a',
    border: '1px solid rgba(106, 27, 154, 0.4)',
  },
  'סיום טיפול טכנאי': {
    bg: '#e8f5e9',
    text: '#2e7d32',
    border: '1px solid rgba(46, 125, 50, 0.45)',
  },
  'חזר ללקוח': {
    bg: '#eceff1',
    text: '#546e7a',
    border: '1px solid rgba(84, 110, 122, 0.4)',
  },
  'חזר לחנות': {
    bg: '#eceff1',
    text: '#546e7a',
    border: '1px solid rgba(84, 110, 122, 0.4)',
  },
  'ממתין לאישור לקוח': {
    bg: '#fff8e1',
    text: '#ef6c00',
    border: '1px solid rgba(239, 108, 0, 0.45)',
  },
  default: {
    bg: '#eceff1',
    text: '#546e7a',
    border: '1px solid rgba(84, 110, 122, 0.35)',
  },
};

/** צבעי סטטוס לרקע כהה — ניגודיות גבוהה + accent מוצק לעיגולים */
const DARK: Record<string, RepairStatusColors> = {
  labPending: {
    bg: 'rgba(59, 130, 246, 0.38)',
    text: '#bfdbfe',
    accent: '#3b82f6',
    border: '1px solid rgba(147, 197, 253, 0.75)',
  },
  returned: {
    bg: 'rgba(148, 163, 184, 0.32)',
    text: '#f1f5f9',
    accent: '#94a3b8',
    border: '1px solid rgba(226, 232, 240, 0.55)',
  },
  'התקבל, ממתין לטיפול': {
    bg: 'rgba(59, 130, 246, 0.38)',
    text: '#bfdbfe',
    accent: '#3b82f6',
    border: '1px solid rgba(147, 197, 253, 0.75)',
  },
  'ממתין למשלוח חזרה': {
    bg: 'rgba(168, 85, 247, 0.36)',
    text: '#f3e8ff',
    accent: '#a855f7',
    border: '1px solid rgba(216, 180, 254, 0.7)',
  },
  'סיום טיפול טכנאי': {
    bg: 'rgba(34, 197, 94, 0.34)',
    text: '#bbf7d0',
    accent: '#22c55e',
    border: '1px solid rgba(134, 239, 172, 0.7)',
  },
  'חזר ללקוח': {
    bg: 'rgba(148, 163, 184, 0.32)',
    text: '#f1f5f9',
    accent: '#94a3b8',
    border: '1px solid rgba(226, 232, 240, 0.55)',
  },
  'חזר לחנות': {
    bg: 'rgba(148, 163, 184, 0.32)',
    text: '#f1f5f9',
    accent: '#94a3b8',
    border: '1px solid rgba(226, 232, 240, 0.55)',
  },
  'ממתין לאישור לקוח': {
    bg: 'rgba(255, 208, 0, 0.22)',
    text: '#ffe566',
    accent: '#e6bb00',
    border: '1px solid rgba(255, 208, 0, 0.7)',
  },
  default: {
    bg: 'rgba(148, 163, 184, 0.3)',
    text: '#e2e8f0',
    accent: '#94a3b8',
    border: '1px solid rgba(203, 213, 225, 0.5)',
  },
};

function resolveStatusKey(status: string): string {
  const s = String(status || '').trim();
  if (s === 'ממתין לטיפול' || s === 'התקבל במעבדה') return 'labPending';
  if (s === 'חזר ללקוח' || s === 'חזר לחנות') return 'returned';
  return s;
}

/** מילוי מוצק לעיגולי סטפר — קריא עם מספר לבן */
export function getRepairStatusFill(colors: RepairStatusColors): string {
  return colors.accent || colors.text;
}

/** צבעי תג סטטוס — טבלאות, מובייל, סטפר */
export function getRepairStatusColors(
  status: string,
  mode: RepairStatusColorMode = 'light',
): RepairStatusColors {
  const palette = mode === 'dark' ? DARK : LIGHT;
  const key = resolveStatusKey(status);
  if (key === 'labPending' || key === 'returned') {
    return palette[key];
  }
  return palette[key] || palette.default;
}

/** לייצוא HTML/PDF — תמיד מצב בהיר */
export function getRepairStatusExportCss(status: string): string {
  const { bg, text, border } = getRepairStatusColors(status, 'light');
  const borderCss = border ? `;border:${border}` : '';
  return `background-color:${bg};color:${text}${borderCss}`;
}

/** שם קודם — לתאימות עם קוד/בנייה שלא עודכנו */
export function getRepairStatusExportStyle(status: string): string {
  return getRepairStatusExportCss(status);
}
